/*
  sm-fx — the motion engine.

  Reads its configuration from the fx-* classes and custom properties that the
  control panel writes, so nothing here needs to know about the panel. Every
  behaviour checks its own flag on each run, which means a setting can be
  turned on or off live without a reload.

  Everything degrades to a fully visible, fully usable page: if this file never
  runs, no element is left hidden, because the hiding rules in sm-fx.css are all
  scoped under a class this file's owner adds.
*/
(function () {
  const root = document.documentElement;
  const on = (name) => root.classList.contains(name);
  const num = (name, fallback) => {
    const v = parseFloat(getComputedStyle(root).getPropertyValue(name));
    return Number.isFinite(v) ? v : fallback;
  };

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionOff = () =>
    (on('fx-respect-motion') && reduced.matches) ||
    (on('fx-no-mobile-motion') && window.innerWidth < 750);

  /* --------------------------------------------------------------------- */
  /* Which elements take part                                              */

  const REVEAL_TARGETS = [
    '.sm-section__head',
    '.sm-pillar',
    '.person-card',
    '.article-card',
    '.sm-result',
    '.sm-steps__item',
    '.sm-feature-split__media',
    '.sm-feature-split__copy',
    '.sm-callout__body',
    '.card-wrapper',
    '.sm-story__body > p',
    '.sm-story__figure',
  ].join(',');

  const HEADING_TARGETS = [
    '.sm-hero__title',
    '.sm-section__heading',
    '.sm-spotlight__title',
    'h1.display',
    'h2.display',
  ].join(',');

  /* --------------------------------------------------------------------- */
  /* Scroll reveal                                                          */

  let revealObserver = null;

  function markReveals() {
    document.querySelectorAll(REVEAL_TARGETS).forEach((el) => {
      /* A card inside a carousel is the wrong unit to reveal: the row runs
         sideways, so most of it sits past the fold and would stay clipped
         until the drift happened to bring it in. The row reveals as one
         thing, through the section heading above it. */
      if (el.closest('sm-carousel')) {
        el.removeAttribute('data-fx-reveal');
        el.classList.remove('fx-in');
        return;
      }
      if (el.hasAttribute('data-fx-reveal')) return;
      el.setAttribute('data-fx-reveal', '');
    });
  }

  function stagger() {
    // The cascade is worked out when items come into view (see startReveal),
    // so nothing here waits on its position in a long grid. Clear any delay
    // left from an earlier pass.
    document.querySelectorAll('[data-fx-reveal]').forEach((el) => el.style.removeProperty('--fx-delay'));
  }

  function startReveal() {
    if (revealObserver) revealObserver.disconnect();
    const once = !on('fx-reveal-repeat');
    const step = num('--fx-reveal-stagger', 60);
    revealObserver = new IntersectionObserver(
      (entries) => {
        // Items that come into view together cascade in reading order (top
        // to bottom, then left to right), starting from zero each time. A
        // card further down a long grid no longer waits for its index, so
        // whatever scrolls in starts moving straight away.
        const arriving = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);
        const dur = num('--fx-reveal-dur', 900);
        arriving.forEach((entry, i) => {
          const delay = Math.min(i, 6) * step;
          const el = entry.target;
          el.style.setProperty('--fx-delay', `${delay}ms`);
          el.classList.add('fx-in');
          // Once the reveal has played, hand the element back to its own
          // transitions (a card's hover lift, for one).
          clearTimeout(el._fxDone);
          el._fxDone = setTimeout(() => el.classList.add('fx-done'), delay + dur * 1.15 + 50);
          if (once) revealObserver.unobserve(el);
        });
        if (!once) {
          entries.forEach((entry) => {
            if (entry.isIntersecting) return;
            clearTimeout(entry.target._fxDone);
            entry.target.classList.remove('fx-in', 'fx-done');
          });
        }
      },
      { rootMargin: `0px 0px -${num('--fx-reveal-trigger', 12)}% 0px`, threshold: 0 }
    );
    document.querySelectorAll('[data-fx-reveal]').forEach((el) => revealObserver.observe(el));
  }

  function showEverything() {
    document.querySelectorAll('[data-fx-reveal]').forEach((el) => el.classList.add('fx-in', 'fx-done'));
    document
      .querySelectorAll('.fx-line > span, .fx-word, .fx-char')
      .forEach((el) => el.classList.add('fx-in'));
  }

  /* --------------------------------------------------------------------- */
  /* Split text                                                             */

  function splitHeading(el, mode) {
    if (el.dataset.fxSplit === mode) return;
    if (el.dataset.fxOriginal === undefined) el.dataset.fxOriginal = el.innerHTML;
    const text = el.dataset.fxOriginal;

    if (mode === 'off') {
      el.innerHTML = text;
      delete el.dataset.fxSplit;
      return;
    }

    /* Split on <br> so a hard break becomes a line, then let the parser turn
       each piece back into plain text. Stripping tags with a regex and
       assigning the result as textContent looked equivalent but was not: it
       leaves entities encoded, so a name like "Dave Claassen &amp; Paul"
       rendered the "&amp;" literally. */
    const decode = (html) => {
      const holder = document.createElement('div');
      holder.innerHTML = html;
      return (holder.textContent || '').trim();
    };
    const lines = text.split(/<br\s*\/?>/i).map(decode);
    el.innerHTML = '';

    let index = 0;
    const step = num('--fx-split-stagger', 45);

    lines.forEach((line) => {
      const lineEl = document.createElement('span');
      lineEl.className = 'fx-line';

      if (mode === 'lines') {
        const inner = document.createElement('span');
        inner.textContent = line;
        inner.style.setProperty('--fx-delay', `${index * step}ms`);
        index += 1;
        lineEl.appendChild(inner);
      } else {
        const units = mode === 'words' ? line.split(/(\s+)/) : Array.from(line);
        units.forEach((unit) => {
          if (/^\s+$/.test(unit)) {
            lineEl.appendChild(document.createTextNode(unit));
            return;
          }
          const span = document.createElement('span');
          span.className = mode === 'words' ? 'fx-word' : 'fx-char';
          span.textContent = unit;
          span.style.setProperty('--fx-delay', `${index * step}ms`);
          index += 1;
          lineEl.appendChild(span);
        });
      }
      el.appendChild(lineEl);
    });

    el.dataset.fxSplit = mode;
    el.setAttribute('data-fx-reveal', '');
  }

  function applySplit() {
    const mode = root.dataset.fxSplitMode || 'off';
    document.querySelectorAll(HEADING_TARGETS).forEach((el) => splitHeading(el, mode));
  }

  /* --------------------------------------------------------------------- */
  /* Scroll progress, header hide, parallax, hero exit                      */

  let ticking = false;
  let lastY = window.scrollY;
  let hideDir = 0;
  let hideAnchor = window.scrollY;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;

      if (on('fx-progress')) {
        root.style.setProperty('--fx-progress', max > 0 ? y / max : 0);
      }

      if (on('fx-headerhide')) {
        const section = document.querySelector('.section-header');
        if (section) {
          // Only switch after a deliberate scroll in one direction. Each
          // change of direction sets an anchor; the header hides once the
          // page has gone 16px down from it and comes back once it has gone
          // 40px up. Trackpad wobble and momentum settling no longer flick
          // the bar in and out. Near the top it always shows.
          if (y <= 240) {
            section.classList.remove('fx-header-away');
            hideDir = 0;
          } else if (y > lastY) {
            if (hideDir !== 1) { hideDir = 1; hideAnchor = lastY; }
            if (y - hideAnchor > 16) section.classList.add('fx-header-away');
          } else if (y < lastY) {
            if (hideDir !== -1) { hideDir = -1; hideAnchor = lastY; }
            if (hideAnchor - y > 40) section.classList.remove('fx-header-away');
          }
        }
      }

      if (on('fx-parallax')) {
        const depth = num('--fx-parallax-depth', 12);
        document.querySelectorAll('[data-fx-parallax]').forEach((el) => {
          const rect = el.getBoundingClientRect();
          const centre = rect.top + rect.height / 2 - window.innerHeight / 2;
          el.style.transform = `translate3d(0, ${(-centre / window.innerHeight) * depth}px, 0)`;
        });
      }

      if (on('fx-hero-exit')) {
        const hero = document.querySelector('.sm-hero');
        if (hero) {
          const h = hero.offsetHeight || 1;
          const p = Math.min(Math.max(y / h, 0), 1);
          root.style.setProperty('--fx-hero-fade', String(1 - p));
          root.style.setProperty('--fx-hero-scale', String(1 - p * 0.06));
        }
      }

      lastY = y;
      ticking = false;
    });
  }

  /* --------------------------------------------------------------------- */
  /* Counters                                                               */

  function startCounters() {
    if (!on('fx-counters')) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);
          const raw = el.dataset.fxCountTo || el.textContent.trim();
          const match = raw.match(/^([^\d]*)([\d,.]+)(.*)$/);
          if (!match) return;
          const [, pre, digits, post] = match;
          const target = parseFloat(digits.replace(/,/g, ''));
          if (!Number.isFinite(target)) return;
          el.dataset.fxCountTo = raw;
          const started = performance.now();
          const dur = 1400;
          const step = (now) => {
            const t = Math.min((now - started) / dur, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            const value = Math.round(target * eased);
            el.textContent = `${pre}${value.toLocaleString()}${post}`;
            if (t < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 }
    );
    document.querySelectorAll('[data-fx-count], .sm-stat__value').forEach((el) => io.observe(el));
  }

  /* --------------------------------------------------------------------- */
  /* Tilt and magnetic                                                      */

  function bindTilt() {
    if (bindTilt.done) return;
    bindTilt.done = true;
    document.addEventListener('pointermove', (e) => {
      if (!on('fx-tilt') || motionOff()) return;
      const card = e.target.closest('.person-card, .article-card');
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const max = num('--fx-card-tilt', 0);
      if (!max) return;
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      card.style.transform = `perspective(900px) rotateY(${px * max}deg) rotateX(${-py * max}deg)`;
    });
    document.addEventListener('pointerout', (e) => {
      const card = e.target.closest && e.target.closest('.person-card, .article-card');
      if (card) card.style.transform = '';
    });
  }

  function bindMagnet() {
    if (bindMagnet.done) return;
    bindMagnet.done = true;
    document.addEventListener('pointermove', (e) => {
      if (!on('fx-magnet') || motionOff()) return;
      const strength = num('--fx-magnet', 0);
      if (!strength) return;
      document.querySelectorAll('.sm-button, .button').forEach((btn) => {
        const rect = btn.getBoundingClientRect();
        const dx = e.clientX - (rect.left + rect.width / 2);
        const dy = e.clientY - (rect.top + rect.height / 2);
        const dist = Math.hypot(dx, dy);
        if (dist < 140) {
          btn.style.transform = `translate(${(dx / 140) * strength}px, ${(dy / 140) * strength}px)`;
        } else if (btn.style.transform) {
          btn.style.transform = '';
        }
      });
    });
  }

  /* --------------------------------------------------------------------- */
  /* Ripple, cart bump, image load-in, drag label                           */

  function bindRipple() {
    if (bindRipple.done) return;
    bindRipple.done = true;
    document.addEventListener('click', (e) => {
      if (!on('fx-ripple')) return;
      const btn = e.target.closest('.sm-button, .button');
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const dot = document.createElement('span');
      dot.className = 'fx-ripple-dot';
      dot.style.left = `${e.clientX - rect.left}px`;
      dot.style.top = `${e.clientY - rect.top}px`;
      dot.style.width = dot.style.height = '12px';
      const pos = getComputedStyle(btn).position;
      if (pos === 'static') btn.style.position = 'relative';
      btn.appendChild(dot);
      setTimeout(() => dot.remove(), 620);
    });
  }

  function bindImageLoad() {
    document.querySelectorAll('img').forEach((img) => {
      if (img.hasAttribute('data-fx-img')) return;
      img.setAttribute('data-fx-img', '');
      if (img.complete && img.naturalWidth) img.classList.add('fx-loaded');
      else img.addEventListener('load', () => img.classList.add('fx-loaded'), { once: true });
    });
  }

  function bindDragLabel() {
    if (bindDragLabel.done) return;
    bindDragLabel.done = true;
    const label = document.createElement('div');
    label.className = 'fx-cursor-label';
    label.textContent = 'Drag';
    document.body.appendChild(label);
    document.addEventListener('pointermove', (e) => {
      const over = on('fx-dragcursor') && e.target.closest('.sm-people--carousel');
      label.classList.toggle('is-on', Boolean(over));
      if (over) {
        label.style.left = `${e.clientX}px`;
        label.style.top = `${e.clientY}px`;
      }
    });
  }

  function bindCartBump() {
    document.addEventListener('click', (e) => {
      if (!on('fx-cartbump')) return;
      if (!e.target.closest('[name="add"], .product-form__submit')) return;
      setTimeout(() => {
        const bubble = document.querySelector('.cart-count-bubble');
        if (!bubble) return;
        bubble.classList.add('fx-bumping');
        setTimeout(() => bubble.classList.remove('fx-bumping'), 460);
      }, 400);
    });
  }

  /* --------------------------------------------------------------------- */
  /* Chrome the engine owns                                                 */

  function ensureChrome() {
    if (!document.querySelector('.fx-grain-layer')) {
      const grain = document.createElement('div');
      grain.className = 'fx-grain-layer';
      document.body.appendChild(grain);
    }
    if (!document.querySelector('.fx-progress-bar')) {
      const bar = document.createElement('div');
      bar.className = 'fx-progress-bar';
      document.body.appendChild(bar);
    }
    document.querySelector('.fx-progress-bar').style.display = on('fx-progress') ? '' : 'none';
  }

  /* --------------------------------------------------------------------- */
  /* Public entry point: called once, and again whenever settings change    */

  function refresh() {
    ensureChrome();
    bindImageLoad();
    bindTilt();
    bindMagnet();
    bindRipple();
    bindDragLabel();
    bindCartBump();

    applySplit();
    markReveals();
    stagger();

    if (motionOff() || !on('fx-reveal')) {
      if (revealObserver) revealObserver.disconnect();
      showEverything();
    } else {
      startReveal();
    }

    startCounters();
    onScroll();
  }

  window.smFx = { refresh, showEverything };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', refresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
})();
